// MIT License - Copyright (c) 2026 Doug McNall
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import './Header.css';

const Header = () => {
  const [menuActive, setMenuActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isDarkMode } = useTheme();
  const { cartItemCount } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setMenuActive(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="header">
        <div className="header_container">
          <div className="container">
            <div className="row">
              <div className="col">
                <div className="header_content d-flex flex-row align-items-center justify-content-start">
                  <div className="logo">
                    <Link to="/shop">
                      <img 
                        src={`${process.env.PUBLIC_URL}/assets/${isDarkMode ? 'dd-logo-light.png' : 'dd-logo-Dark.png'}`}
                        alt="D&D Hotsauce"
                        className="logo_image"
                      />
                    </Link>
                  </div>
                  <nav className="main_nav">
                    <ul>
                      <li className="active">
                        <Link to="/shop">Home</Link>
                      </li>
                      <li><Link to="/shop/product">Products</Link></li>
                      <li><Link to="/shop/categories">Categories</Link></li>
                      <li><Link to="/shop/recipes">Recipes</Link></li>
                      <li><Link to="/shop/offers">Offers</Link></li>
                    </ul>
                  </nav>
                  
                  {/* Search Bar */}
                  <form className="header_search" onSubmit={handleSearch}>
                    <input 
                      type="text" 
                      className="search_input" 
                      placeholder="Search products..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button type="submit" className="search_button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                    </button>
                  </form>

                  {isAuthenticated && user?.attributes?.name && (
                    <div className="welcome_message">
                      <span className="welcome_text">Welcome Back, </span>
                      <span className="welcome_name">{user.attributes.name}!</span>
                    </div>
                  )}
                  <div className="header_extra ml-auto">
                    {!isAuthenticated ? (
                      <div className="login_link">
                        <Link to="/shop/login">
                          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                            <polyline points="10 17 15 12 10 7"></polyline>
                            <line x1="15" y1="12" x2="3" y2="12"></line>
                          </svg>
                          <div>Login</div>
                        </Link>
                      </div>
                    ) : (
                      <div className="login_link logout_link">
                        <button onClick={handleLogout} className="logout-button">
                          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                          </svg>
                          <div>Logout</div>
                        </button>
                      </div>
                    )}
                    {isAuthenticated && (
                      <div className="account_link">
                        <Link to="/shop/account">
                          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 1-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                          <div>Account</div>
                        </Link>
                      </div>
                    )}
                    <div className="need_help">
                      <Link to="/shop/help">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                          <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <div>Need Help?</div>
                      </Link>
                    </div>
                    <div className="shopping_cart">
                      <Link to="/shop/cart">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 489 489">
                          <g>
                            <path d="M440.1,422.7l-28-315.3c-0.6-7-6.5-12.3-13.4-12.3h-57.6C340.3,42.5,297.3,0,244.5,0s-95.8,42.5-96.6,95.1H90.3c-7,0-12.8,5.3-13.4,12.3l-28,315.3c0,0.4-0.1,0.8-0.1,1.2c0,35.9,32.9,65.1,73.4,65.1h244.6c40.5,0,73.4-29.2,73.4-65.1C440.2,423.5,440.2,423.1,440.1,422.7z M244.5,27c37.9,0,68.8,30.4,69.6,68.1H174.9C175.7,57.4,206.6,27,244.5,27z M366.8,462H122.2c-25.4,0-46-16.8-46.4-37.5l26.8-302.3h45.2v41c0,7.5,6,13.5,13.5,13.5s13.5-6,13.5-13.5v-41h139.3v41c0,7.5,6,13.5,13.5,13.5s13.5-6,13.5-13.5v-41h45.2l26.9,302.3C412.8,445.2,392.1,462,366.8,462z" />
                          </g>
                        </svg>
                        <div>Cart <span>({cartItemCount})</span></div>
                      </Link>
                    </div>
                    <ThemeToggle />
                    <div className="hamburger" onClick={() => setMenuActive(!menuActive)}>
                      <i className="fa fa-bars" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`menu menu_mm trans_300 ${menuActive ? 'active' : ''}`}>
        <div className="menu_container menu_mm">
          <div className="page_menu_content">
            {/* Mobile Search */}
            <form className="mobile_search" onSubmit={handleSearch}>
              <input 
                type="text" 
                placeholder="Search products..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit">
                <i className="fa fa-search" aria-hidden="true"></i>
              </button>
            </form>
            
            <ul className="page_menu_nav menu_mm">
              <li className="page_menu_item has-children menu_mm">
                <Link to="/shop">Home<i className="fa fa-angle-down"></i></Link>
              </li>
              <li className="page_menu_item menu_mm">
                <Link to="/shop/product">Products<i className="fa fa-angle-down"></i></Link>
              </li>
              <li className="page_menu_item menu_mm">
                <Link to="/shop/categories">Categories<i className="fa fa-angle-down"></i></Link>
              </li>
              <li className="page_menu_item menu_mm">
                <Link to="/shop/recipes">Recipes<i className="fa fa-angle-down"></i></Link>
              </li>
              <li className="page_menu_item menu_mm">
                <Link to="/shop/offers">Offers<i className="fa fa-angle-down"></i></Link>
              </li>
              {!isAuthenticated ? (
                <li className="page_menu_item menu_mm">
                  <Link to="/shop/login">Login</Link>
                </li>
              ) : (
                <>
                  <li className="page_menu_item menu_mm">
                    <Link to="/shop/account">Account</Link>
                  </li>
                  <li className="page_menu_item menu_mm">
                    <button onClick={handleLogout} className="mobile-logout-button">Logout</button>
                  </li>
                </>
              )}
              <li className="page_menu_item menu_mm">
                <Link to="/shop/help">Need Help?</Link>
                <Link to="/shop/support">Contact Support</Link>
              </li>
              <li className="page_menu_item menu_mm mobile_theme_toggle">
                <ThemeToggle />
              </li>
            </ul>
          </div>
        </div>
        <div className="menu_close" onClick={() => setMenuActive(false)}>
          <i className="fa fa-times" aria-hidden="true"></i>
        </div>
        <div className="menu_social">
          <ul>
            <li><button type="button" aria-label="Pinterest"><i className="fa fa-pinterest" aria-hidden="true"></i></button></li>
            <li><button type="button" aria-label="Instagram"><i className="fa fa-instagram" aria-hidden="true"></i></button></li>
            <li><button type="button" aria-label="Facebook"><i className="fa fa-facebook" aria-hidden="true"></i></button></li>
            <li><button type="button" aria-label="Twitter"><i className="fa fa-twitter" aria-hidden="true"></i></button></li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Header;
